import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { UserPlus } from 'lucide-react';

export default function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    fleetName: '',
    businessRegistrationNumber: '',
    address: '',
    fleetPhone: '',
    fleetEmail: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const register = useAuthStore((state) => state.register);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const personalEmail = formData.email.trim();
    if (!personalEmail.includes('@')) {
      setError('Please enter a valid email address containing @.');
      return;
    }
    const fleetEmailTrim = formData.fleetEmail.trim();
    if (fleetEmailTrim && !fleetEmailTrim.includes('@')) {
      setError('Fleet email must contain @.');
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData);
      
      if (result.success) {
        // Verify localStorage is set
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        
        if (token && user) {
          // Navigate to dashboard after successful registration
          navigate('/dashboard', { replace: true });
        } else {
          setError('Registration succeeded but session could not be saved. Please try again.');
          setLoading(false);
        }
      } else {
        setError(result.error || 'Registration failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-surface dark:bg-surface-dark rounded-2xl shadow-lg p-8 border border-border dark:border-border-dark">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-text dark:text-text-dark mb-2">
              Create Fleet Account
            </h1>
            <p className="text-text-secondary dark:text-text-secondary-dark">
              Register your fleet to get started
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-danger-light dark:bg-danger-dark border border-danger rounded-lg p-3 text-danger text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="John"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                Your Email * <span className="text-text-tertiary dark:text-text-tertiary-dark text-xs font-normal">(for your account)</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter your password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                Your Phone * <span className="text-text-tertiary dark:text-text-tertiary-dark text-xs font-normal">(for your account)</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1234567890"
              />
            </div>

            <div className="border-t border-border dark:border-border-dark pt-6">
              <h3 className="text-lg font-semibold text-text dark:text-text-dark mb-4">Fleet Business Information</h3>
              <p className="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
                Provide your fleet business details. Contact information can be different from your personal contact if needed.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                Fleet Name *
              </label>
              <input
                type="text"
                name="fleetName"
                value={formData.fleetName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="ABC Transport Fleet"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                Business Registration Number
              </label>
              <input
                type="text"
                name="businessRegistrationNumber"
                value={formData.businessRegistrationNumber}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="REG123456"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="123 Main St, City, State"
              />
            </div>

            <div className="bg-info-light dark:bg-info-dark border border-info rounded-lg p-4">
              <p className="text-sm text-info">
                <strong>Note:</strong> If fleet contact information is different from your personal contact, you can specify it below. Otherwise, your personal email and phone will be used for the fleet.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                  Fleet Phone <span className="text-text-tertiary dark:text-text-tertiary-dark text-xs">(optional)</span>
                </label>
                <input
                  type="tel"
                  name="fleetPhone"
                  value={formData.fleetPhone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Leave empty to use your phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
                  Fleet Email <span className="text-text-tertiary dark:text-text-tertiary-dark text-xs">(optional)</span>
                </label>
                <input
                  type="email"
                  name="fleetEmail"
                  value={formData.fleetEmail}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-lg text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Leave empty to use your email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            <div className="text-center">
              <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:text-primary-dark font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
